// apps/web/src/shared/identifierTakenMeta.ts
//
// Defensive narrowing for the CRM v4 identity-conflict 409 (#2242 / #2247 M6).
// The wire envelope is a CLOSED `DOMAIN_CONFLICT` carrying the discriminator in
// `meta.kind === 'IDENTIFIER_TAKEN'`, plus — when the taken identifier resolves
// to a live Client — `meta.owner = { clientId, managerId }` (ids only, NEVER
// contact PII). `meta.identity.value` is the RAW submitted identifier and MUST
// NOT be rendered. Mirrors `phoneExistsMeta.ts` (the legacy phone-only shape).
import { IDENTIFIER_TAKEN_KIND, type IdentifierTakenOwner } from '@magimp/shared';

/**
 * @aiPurpose narrowed view of an `ApiClientError.meta` for the CRM v4
 *   IDENTIFIER_TAKEN 409. `owner` is `null` when the conflict cannot be resolved
 *   to a live client owner (a transitional lead/engagement owner) — the caller
 *   then shows a generic "already in use" message with no navigation target.
 * @aiSecurity `owner` carries ONLY ids (client + manager) — never a phone,
 *   name, email, or the raw identifier. Callers route to the owning client by
 *   id; they must never echo the colliding identifier.
 */
export interface IdentifierTakenInfo {
  owner: IdentifierTakenOwner | null;
}

/**
 * @aiPurpose narrow the untyped `ApiClientError.meta` of a `DOMAIN_CONFLICT` 409
 *   into the IDENTIFIER_TAKEN shape. Returns `null` when the meta is not the
 *   identity-taken discriminator, so the caller falls back to a generic toast.
 */
export function parseIdentifierTakenMeta(meta: unknown): IdentifierTakenInfo | null {
  if (meta == null || typeof meta !== 'object') return null;
  const m = meta as Record<string, unknown>;
  if (m['kind'] !== IDENTIFIER_TAKEN_KIND) return null;
  const owner = m['owner'];
  if (owner != null && typeof owner === 'object') {
    const o = owner as Record<string, unknown>;
    const clientId = o['clientId'];
    const managerId = o['managerId'];
    if (typeof clientId === 'string' && typeof managerId === 'string') {
      return { owner: { clientId, managerId } };
    }
  }
  return { owner: null };
}
